import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useClassifier } from "@/hooks/useClassifier";
import { useLlmModels } from "@/hooks/useLlmModels";
import { supabase } from "@/integrations/supabase/client";
import { DEFAULT_CLASSIFICATION_LLM_MODEL_KEY, type LlmModelKey } from "@smartmed/llm-models";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpDown, ChevronDown, ChevronRight, Filter } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { getPatientEncounterLabel } from "@/test-data/patientEncounterLabel";
import { MetaStatusIndicators } from "./MetaStatusIndicators";

interface TriggerRunDialogProps {
  open: boolean;
  onClose: () => void;
  onRunsTriggered?: () => void;
}

interface EnhancedMedicalTest {
  id: string;
  sequence_number: number;
  label: string | null;
  test_type: "Endoscopy" | "Histology" | "GP Summary" | "Referral" | (string & {}) | null;
  text: string | null;
  created_at: string | null;
  checked_by_coder: boolean;
  issue_count: number;
  active_issues: number;
  patient: {
    id: string | null;
  } | null;
  encounter: {
    id: string | null;
    integration_encounter_id: string | null;
  } | null;
}

type CoderFilter = "all" | "approved" | "pending";
type IssuesFilter = "all" | "no-issues" | "active-issues" | "resolved-only";
type SortField = "created_at" | "coder_status" | "issues_status" | "sequence_number";
type SortOrder = "asc" | "desc";

export function TriggerRunDialog({ open, onClose, onRunsTriggered }: TriggerRunDialogProps) {
  const [selectedTests, setSelectedTests] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedLlmModelId, setSelectedLlmModelId] = useState<LlmModelKey | undefined>(undefined);

  // Phase 2: Filtering and sorting state
  const [coderFilter, setCoderFilter] = useState<CoderFilter>("all");
  const [issuesFilter, setIssuesFilter] = useState<IssuesFilter>("all");
  const [sortField, setSortField] = useState<SortField>("created_at");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  // New state for collapsible filters
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  const { classifyTestData } = useClassifier();

  const {
    data: medicalTests,
    isLoading: isMedicalTestsLoading,
    error: medicalTestsError,
  } = useQuery({
    queryKey: ["test-run-data-stages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("medical_text_examples")
        .select(
          `
          *,
          patient_medical_text_examples!left(
            patient_id,
            patients!inner(
              id
            )
          ),
          patient_encounter_medical_text_examples!left(
            patient_encounter_id,
            patient_encounters!inner(
              id,
              admit_datetime
            )
          )
        `,
        )
        .not("test_type", "eq", "Histology")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching medical tests:", error);
        throw error;
      }

      // Now fetch issue counts for each test
      const enhancedData: EnhancedMedicalTest[] = await Promise.all(
        data.map(async (test) => {
          // Get total issue count
          const { count: totalIssues, error: totalError } = await supabase
            .from("medical_text_issues")
            .select("*", { count: "exact", head: true })
            .eq("medical_text_example_id", test.id);

          if (totalError) {
            console.error("Error fetching total issues for test", test.id, totalError);
          }

          // Get active issue count
          const { count: activeIssues, error: activeError } = await supabase
            .from("medical_text_issues")
            .select("*", { count: "exact", head: true })
            .eq("medical_text_example_id", test.id)
            .eq("status", "active");

          if (activeError) {
            console.error("Error fetching active issues for test", test.id, activeError);
          }

          return {
            id: test.id,
            label: test.label,
            sequence_number: test.sequence_number,
            test_type: test.test_type,
            text: test.text,
            created_at: test.created_at,
            checked_by_coder: test.checked_by_coder,
            issue_count: totalIssues || 0,
            active_issues: activeIssues || 0,
            patient: test.patient_medical_text_examples
              ? {
                  id: test.patient_medical_text_examples?.patients?.id,
                }
              : null,
            encounter: test.patient_encounter_medical_text_examples
              ? {
                  id: test.patient_encounter_medical_text_examples?.patient_encounter_id,
                  integration_encounter_id: getPatientEncounterLabel(
                    test.patient_encounter_medical_text_examples?.patient_encounters,
                  ),
                }
              : null,
          };
        }),
      );

      return enhancedData;
    },
  });

  // Phase 2: Filter and sort medical tests
  const filteredAndSortedTests = medicalTests
    ?.filter((test) => {
      // Apply coder filter
      if (coderFilter === "approved" && !test.checked_by_coder) return false;
      if (coderFilter === "pending" && test.checked_by_coder) return false;

      // Apply issues filter
      if (issuesFilter === "no-issues" && test.issue_count > 0) return false;
      if (issuesFilter === "active-issues" && test.active_issues === 0) return false;
      if (issuesFilter === "resolved-only" && (test.issue_count === 0 || test.active_issues > 0)) return false;

      return true;
    })
    ?.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case "created_at":
          comparison = new Date(a.created_at ?? 0).getTime() - new Date(b.created_at ?? 0).getTime();
          break;
        case "coder_status":
          comparison = Number(b.checked_by_coder) - Number(a.checked_by_coder);
          break;
        case "issues_status":
          // Sort by active issues first, then total issues
          if (a.active_issues !== b.active_issues) {
            comparison = a.active_issues - b.active_issues;
          } else {
            comparison = a.issue_count - b.issue_count;
          }
          break;
        case "sequence_number":
          comparison = a.sequence_number - b.sequence_number;
          break;
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

  useEffect(() => {
    if (medicalTestsError) {
      toast.error("Failed to fetch medical tests");
    }
  }, [medicalTestsError]);

  const { data: llmModels, isLoading: isLlmModelsLoading, error: llmModelsError } = useLlmModels();

  useEffect(() => {
    if (llmModels?.some((model) => model.id === DEFAULT_CLASSIFICATION_LLM_MODEL_KEY)) {
      setSelectedLlmModelId(DEFAULT_CLASSIFICATION_LLM_MODEL_KEY);
    }
  }, [llmModels]);

  useEffect(() => {
    if (llmModelsError) {
      toast.error("Failed to fetch LLM models");
    }
  }, [llmModelsError]);

  const handleSubmit = async () => {
    if (selectedTests.length === 0) {
      toast.error("Please select at least one test to run");
      return;
    }
    if (!selectedLlmModelId) {
      toast.error("Please select an LLM model");
      return;
    }
    setIsSubmitting(true);
    try {
      const results = await Promise.allSettled(
        selectedTests.map((testId) => classifyTestData(testId, selectedLlmModelId)),
      );

      const failedRuns = results.filter((result) => result.status === "rejected" || !!result.value.error);
      const successfulRuns = results.filter((result) => result.status === "fulfilled" && !result.value.error);

      failedRuns.forEach((result) => {
        toast.error(
          `Failed to start run: ${
            (result as PromiseRejectedResult).reason || (result as { value: { error: string } }).value.error
          }`,
        );
      });

      if (successfulRuns.length > 0) {
        toast.success(`Started ${successfulRuns.length} classification run${successfulRuns.length > 1 ? "s" : ""}`);
      }

      onClose();

      // Call the onRunsTriggered callback after successful completion
      if (onRunsTriggered) {
        onRunsTriggered();
      }
    } catch (error) {
      console.error("Error triggering runs:", error);
      toast.error("Failed to trigger classification runs");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleSelectAll = () => {
    const testsToSelect = filteredAndSortedTests || [];
    if (selectedTests.length === testsToSelect.length) {
      setSelectedTests([]);
    } else {
      setSelectedTests(testsToSelect.map((test) => test.id));
    }
  };

  const toggleTest = (testId: string) => {
    setSelectedTests((prev) => (prev.includes(testId) ? prev.filter((id) => id !== testId) : [...prev, testId]));
  };

  // Phase 2: Bulk selection functions
  const selectApprovedOnly = () => {
    const approvedTests = filteredAndSortedTests?.filter((test) => test.checked_by_coder) || [];
    setSelectedTests(approvedTests.map((test) => test.id));
  };

  const selectNoIssuesOnly = () => {
    const noIssueTests = filteredAndSortedTests?.filter((test) => test.issue_count === 0) || [];
    setSelectedTests(noIssueTests.map((test) => test.id));
  };

  const selectApprovedWithNoIssues = () => {
    const qualityTests =
      filteredAndSortedTests?.filter((test) => test.checked_by_coder && test.issue_count === 0) || [];
    setSelectedTests(qualityTests.map((test) => test.id));
  };

  // Phase 2: Sort handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // Helper function to render the test type badge with appropriate styling
  const renderTestTypeBadge = (testType: string | null) => {
    if (!testType) return <Badge variant="outline">Unknown</Badge>;

    if (testType === "Endoscopy") {
      return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">Endoscopy</Badge>;
    } else if (testType === "Histology") {
      return <Badge className="bg-green-100 text-green-800 hover:bg-green-200">Histology</Badge>;
    } else if (testType === "GP Summary") {
      return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200">GP Summary</Badge>;
    } else if (testType === "Referral") {
      return <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-200">Referral</Badge>;
    } else {
      return <Badge variant="outline">{testType}</Badge>;
    }
  };

  const isLoading = isLlmModelsLoading || isMedicalTestsLoading;

  return (
    <TooltipProvider>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-7xl">
          <div className="flex flex-col h-[calc(100vh-4rem)]">
            <DialogHeader className="flex-none pb-6">
              <DialogTitle>Select Tests to Run</DialogTitle>
              <DialogDescription>
                Choose the test data you want to include in this classification run. Use filters and bulk selection
                tools to quickly select the right tests.
              </DialogDescription>
            </DialogHeader>

            {isLoading ? (
              <div className="flex-1 flex items-center justify-center">Loading...</div>
            ) : (
              <div className="flex flex-col flex-1 min-h-0">
                <div className="mb-4 flex items-center space-x-4">
                  <label className="text-sm font-medium">LLM Model:</label>
                  <Select
                    value={selectedLlmModelId}
                    onValueChange={(value) => setSelectedLlmModelId(value as LlmModelKey)}
                    disabled={isLlmModelsLoading || !llmModels}
                  >
                    <SelectTrigger className="w-64">
                      <SelectValue placeholder="Select a model" />
                    </SelectTrigger>
                    <SelectContent>
                      {llmModels?.map((model) => (
                        <SelectItem key={model.id} value={model.id}>
                          {model.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Collapsible Filters and Bulk Selection */}
                <Collapsible open={isFiltersExpanded} onOpenChange={setIsFiltersExpanded}>
                  <CollapsibleTrigger asChild>
                    <Button variant="outline" className="mb-4 w-full justify-between">
                      <div className="flex items-center gap-2">
                        <Filter className="h-4 w-4" />
                        Filters & Bulk Actions
                      </div>
                      {isFiltersExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <div className="mb-4 p-4 bg-muted/50 rounded-lg space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                          <label className="text-xs font-medium text-muted-foreground">Coder Status</label>
                          <Select value={coderFilter} onValueChange={(value: CoderFilter) => setCoderFilter(value)}>
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All</SelectItem>
                              <SelectItem value="approved">Approved Only</SelectItem>
                              <SelectItem value="pending">Pending Only</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <label className="text-xs font-medium text-muted-foreground">Issues Status</label>
                          <Select value={issuesFilter} onValueChange={(value: IssuesFilter) => setIssuesFilter(value)}>
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All</SelectItem>
                              <SelectItem value="no-issues">No Issues</SelectItem>
                              <SelectItem value="active-issues">Active Issues</SelectItem>
                              <SelectItem value="resolved-only">Resolved Only</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <label className="text-xs font-medium text-muted-foreground">Sort By</label>
                          <Select value={sortField} onValueChange={(value: SortField) => setSortField(value)}>
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="created_at">Created Date</SelectItem>
                              <SelectItem value="sequence_number">ID</SelectItem>
                              <SelectItem value="coder_status">Coder Status</SelectItem>
                              <SelectItem value="issues_status">Issues Status</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <label className="text-xs font-medium text-muted-foreground">Sort Order</label>
                          <Select value={sortOrder} onValueChange={(value: SortOrder) => setSortOrder(value)}>
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="asc">Ascending</SelectItem>
                              <SelectItem value="desc">Descending</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={selectApprovedOnly} className="h-7 text-xs">
                          Select Approved
                        </Button>
                        <Button variant="outline" size="sm" onClick={selectNoIssuesOnly} className="h-7 text-xs">
                          Select No Issues
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={selectApprovedWithNoIssues}
                          className="h-7 text-xs"
                        >
                          Select Quality Tests
                        </Button>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                <div className="flex-1 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox
                            checked={
                              filteredAndSortedTests &&
                              selectedTests.length === filteredAndSortedTests.length &&
                              filteredAndSortedTests.length > 0
                            }
                            onCheckedChange={toggleSelectAll}
                          />
                        </TableHead>
                        <TableHead className="w-16">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 p-0 hover:bg-transparent"
                            onClick={() => handleSort("sequence_number")}
                          >
                            ID
                            <ArrowUpDown className="ml-1 h-3 w-3" />
                          </Button>
                        </TableHead>
                        <TableHead className="w-24">Type</TableHead>
                        <TableHead className="w-20">Meta</TableHead>
                        <TableHead>Text</TableHead>
                        <TableHead className="w-32">Patient</TableHead>
                        <TableHead className="w-32">Encounter</TableHead>
                        <TableHead className="w-48">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 p-0 hover:bg-transparent"
                            onClick={() => handleSort("created_at")}
                          >
                            Created At
                            <ArrowUpDown className="ml-1 h-3 w-3" />
                          </Button>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAndSortedTests?.map((test) => (
                        <TableRow key={test.id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedTests.includes(test.id)}
                              onCheckedChange={() => toggleTest(test.id)}
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            {test.label}{" "}
                            {`${test.sequence_number}` !== test.label && (
                              <span className="text-gray-400 italic">({test.sequence_number})</span>
                            )}
                          </TableCell>
                          <TableCell>{renderTestTypeBadge(test.test_type)}</TableCell>
                          <TableCell>
                            <MetaStatusIndicators
                              checkedByCoder={test.checked_by_coder}
                              issueCount={test.issue_count}
                              activeIssues={test.active_issues}
                            />
                          </TableCell>
                          <TableCell className="max-w-md truncate">
                            {(test.text ?? "").length > 100 ? `${(test.text ?? "").slice(0, 100)}...` : test.text}
                          </TableCell>
                          <TableCell>{test.patient?.id || "No patient"}</TableCell>
                          <TableCell>{test.encounter?.integration_encounter_id || "No encounter"}</TableCell>
                          <TableCell>{dayjs(test.created_at).tz().format("DD/MM/YY HH:mm")}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex-none pt-6 border-t mt-6">
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-muted-foreground">
                      {selectedTests.length} test
                      {selectedTests.length !== 1 ? "s" : ""} selected
                      {filteredAndSortedTests && filteredAndSortedTests.length !== medicalTests?.length && (
                        <span className="ml-2">
                          ({filteredAndSortedTests.length} of {medicalTests?.length} shown)
                        </span>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <Button variant="outline" onClick={onClose}>
                        Cancel
                      </Button>
                      <Button onClick={handleSubmit} disabled={isSubmitting || !selectedLlmModelId}>
                        {isSubmitting ? "Starting..." : "Start Run"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
